import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaLinkedinIn, FaYoutube } from "react-icons/fa";
import { FaAddressCard, FaMedium } from "react-icons/fa6";
import { FiSettings } from "react-icons/fi";
import { HiOutlineAcademicCap } from "react-icons/hi2";
import { Magnetic } from "@/components/interactive/Magnetic";
import { useScrollLock } from "@/hooks/useScrollLock";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { getSocialMediaLink } from "@/utils/external-links";
import { getScrollBehavior } from "@/utils/scroll";
import "../styles/topnav.css";

export type TopNavSection = {
    id: string;
    title: string;
};

type TopNavProps = {
    sections: TopNavSection[];
    onOpenSettings: () => void;
};

const NAV_OFFSET = 72;

function scrollToId(id: string) {
    const element = document.getElementById(id);
    if (!element) return;
    const top = element.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top, behavior: getScrollBehavior() });
}

/** Tracks which section sits under a line 30% down the viewport. */
function useActiveSection(sections: TopNavSection[]) {
    const [activeId, setActiveId] = useState<string | null>(null);

    const update = useCallback(() => {
        const triggerLine = window.innerHeight * 0.3;
        let current: string | null = null;
        for (const { id } of sections) {
            const element = document.getElementById(id);
            if (element && element.getBoundingClientRect().top <= triggerLine) current = id;
        }
        if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 40) {
            current = sections[sections.length - 1]?.id ?? current;
        }
        setActiveId(current);
    }, [sections]);

    useEffect(() => {
        let ticking = false;
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                update();
                ticking = false;
            });
        };
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [update]);

    return activeId;
}

/** Hides the bar while scrolling down past the hero and brings it back on any upward scroll. */
function useHideOnScroll() {
    const [hidden, setHidden] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        let last = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            setScrolled(y > 24);
            if (Math.abs(y - last) < 6) return;
            setHidden(y > last && y > 480);
            last = y;
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return { hidden, scrolled };
}

export default function TopNav({ sections, onOpenSettings }: TopNavProps) {
    const { t } = useTranslation();
    const { locale = "en" } = useParams<{ locale: string }>();
    const activeId = useActiveSection(sections);
    const { hidden, scrolled } = useHideOnScroll();
    const [menuOpen, setMenuOpen] = useState(false);
    const reducedMotion = usePrefersReducedMotion();
    useScrollLock(menuOpen);

    useEffect(() => {
        if (!menuOpen) return;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") setMenuOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [menuOpen]);

    const goTo = (id: string) => {
        setMenuOpen(false);
        // Let the overlay unlock scrolling before measuring the target.
        requestAnimationFrame(() => scrollToId(id));
    };

    const utilityLinks = (
        <>
            <Link className="topnav-icon" to={`/${locale}/card`} aria-label={t("topNav.businessCard")}>
                <FaAddressCard aria-hidden="true" />
            </Link>
            <a className="topnav-icon" href={getSocialMediaLink("linkedin")} target="_blank" rel="noreferrer" aria-label={t("topNav.linkedin")}>
                <FaLinkedinIn aria-hidden="true" />
            </a>
            <a className="topnav-icon" href={getSocialMediaLink("medium")} target="_blank" rel="noreferrer" aria-label={t("topNav.medium")}>
                <FaMedium aria-hidden="true" />
            </a>
            <Link className="topnav-icon" to={`/${locale}/loop-breaker`} aria-label={t("topNav.youtube")}>
                <FaYoutube aria-hidden="true" />
            </Link>
            <Link className="topnav-icon" to={`/${locale}/coaching`} aria-label={t("topNav.coaching")}>
                <HiOutlineAcademicCap aria-hidden="true" />
            </Link>
            <button
                type="button"
                className="topnav-icon"
                aria-label={t("topNav.settings")}
                onClick={(event) => {
                    event.stopPropagation();
                    setMenuOpen(false);
                    onOpenSettings();
                }}
            >
                <FiSettings aria-hidden="true" />
            </button>
        </>
    );

    return (
        <>
            <header className={`topnav ${hidden && !menuOpen ? "is-hidden" : ""} ${scrolled || menuOpen ? "is-scrolled" : ""}`}>
                <div className="topnav-inner">
                    <button
                        type="button"
                        className="topnav-brand"
                        onClick={() => {
                            setMenuOpen(false);
                            window.scrollTo({ top: 0, behavior: getScrollBehavior() });
                        }}
                    >
                        Nicholas Y<span className="topnav-brand-dot">.</span>
                    </button>

                    <nav className="topnav-links" aria-label={t("topNav.sections")}>
                        {sections.map(({ id, title }) => (
                            <button
                                key={id}
                                type="button"
                                className={`topnav-link ${activeId === id ? "is-active" : ""}`}
                                aria-current={activeId === id ? "true" : undefined}
                                onClick={() => goTo(id)}
                            >
                                {title}
                            </button>
                        ))}
                    </nav>

                    <div className="topnav-actions">
                        <div className="topnav-utility">{utilityLinks}</div>
                        <Magnetic strength={0.25}>
                            <button type="button" className="topnav-cta" onClick={() => goTo("contact")}>
                                {t("FloatingCTA.label")}
                            </button>
                        </Magnetic>
                        <button
                            type="button"
                            className={`topnav-menu-toggle ${menuOpen ? "is-open" : ""}`}
                            aria-expanded={menuOpen}
                            aria-controls="topnav-overlay"
                            onClick={() => setMenuOpen((open) => !open)}
                        >
                            <span className="topnav-menu-label">{menuOpen ? t("topNav.close") : t("topNav.menu")}</span>
                            <span className="topnav-menu-bars" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        id="topnav-overlay"
                        className="topnav-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-label={t("topNav.menu")}
                        initial={{ clipPath: "inset(0 0 100% 0)" }}
                        animate={{ clipPath: "inset(0 0 0% 0)" }}
                        exit={{ clipPath: "inset(0 0 100% 0)" }}
                        transition={{ duration: reducedMotion ? 0 : 0.55, ease: [0.76, 0, 0.24, 1] }}
                    >
                        <nav className="topnav-overlay-links" aria-label={t("topNav.sections")}>
                            {sections.map(({ id, title }, index) => (
                                <motion.button
                                    key={id}
                                    type="button"
                                    className={`topnav-overlay-link ${activeId === id ? "is-active" : ""}`}
                                    onClick={() => goTo(id)}
                                    initial={{ opacity: 0, y: 28 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: reducedMotion ? 0 : 0.5, delay: reducedMotion ? 0 : 0.15 + index * 0.05 }}
                                >
                                    {title}
                                </motion.button>
                            ))}
                        </nav>
                        <div className="topnav-overlay-footer">{utilityLinks}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
